import React, { useState, useEffect } from 'react';
import {
  Phone, PhoneOff, PhoneForwarded, PhoneIncoming,
  User, Users, Pause, Play, Circle, ArrowRight,
  Headphones, Volume2, Settings, RefreshCw, Loader2,
  UserCheck, PhoneMissed, Clock
} from 'lucide-react';
import api from '../../services/api';

const PhoneConsole = ({ currentExtension, onDialNumber, callState, connectionState, activeCall }) => {
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forwardingEnabled, setForwardingEnabled] = useState(false);
  const [forwardNumber, setForwardNumber] = useState('');
  const [showForwardingSettings, setShowForwardingSettings] = useState(false);
  const [parkSlots, setParkSlots] = useState([
    { id: 1, occupied: false, callerInfo: null },
    { id: 2, occupied: false, callerInfo: null },
    { id: 3, occupied: false, callerInfo: null },
  ]);

  useEffect(() => {
    loadExtensions();
    const interval = setInterval(loadExtensions, 15000); // Refresh every 15s for real-time status
    return () => clearInterval(interval);
  }, []);

  const loadExtensions = async () => {
    try {
      const response = await api.get('/voip/extensions');
      const exts = (response.data || []).map(ext => ({
        id: ext.id,
        extension: ext.extension,
        name: ext.display_name || 'Ext ' + ext.extension,
        presence: ext.status === 'available' ? 'Available' : ext.status === 'busy' ? 'Busy' : 'Offline',
        onCall: ext.status === 'oncall',
        dndStatus: ext.status === 'dnd' ? 'DoNotAcceptAnyCalls' : null,
        directDid: ext.direct_did,
      }));
      setExtensions(exts);
    } catch (error) {
      console.error('Error loading extensions:', error);
      setExtensions([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (ext) => {
    if (ext.onCall) return 'bg-red-500 animate-pulse';
    if (ext.dndStatus === 'DoNotAcceptAnyCalls') return 'bg-purple-500';
    switch (ext.presence) {
      case 'Available': return 'bg-green-500';
      case 'Busy': return 'bg-red-500';
      case 'Away': return 'bg-yellow-500';
      case 'DoNotDisturb': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBgColor = (ext) => {
    if (ext.onCall) return 'bg-red-500/10 border-red-500/30';
    if (ext.dndStatus === 'DoNotAcceptAnyCalls') return 'bg-purple-500/10 border-purple-500/30';
    switch (ext.presence) {
      case 'Available': return 'bg-green-500/10 border-green-500/30';
      case 'Busy': return 'bg-red-500/10 border-red-500/30';
      case 'Away': return 'bg-yellow-500/10 border-yellow-500/30';
      default: return 'bg-gray-500/10 border-gray-500/30';
    }
  };

  const getStatusLabel = (ext) => {
    if (ext.onCall) {
      const call = ext.activeCalls?.[0];
      if (call?.direction === 'Inbound') return 'Incoming';
      if (call?.direction === 'Outbound') return 'On Call';
      return 'On Call';
    }
    if (ext.dndStatus === 'DoNotAcceptAnyCalls') return 'DND';
    if (ext.telephonyStatus === 'Ringing') return 'Ringing';
    if (ext.telephonyStatus === 'OnHold') return 'On Hold';
    return ext.presence || 'Offline';
  };

  const handleTransferTo = (ext) => {
    // TODO: Implement attended transfer
    console.log('Transfer to extension:', ext.extension);
  };

  const handleCallExtension = (ext) => {
    if (onDialNumber) {
      onDialNumber(ext.extension);
    }
  };

  return (
    <div className="w-72 space-y-4">
      {/* Connection Status */}
      <div className={`px-4 py-3 rounded-xl border flex items-center gap-3 ${
        connectionState === 'registered' ? 'bg-green-500/10 border-green-500/30' :
        connectionState === 'connecting' ? 'bg-yellow-500/10 border-yellow-500/30' :
        connectionState === 'error' ? 'bg-red-500/10 border-red-500/30' :
        'bg-gray-500/10 border-gray-500/30'
      }`}>
        <div className={`w-3 h-3 rounded-full ${
          connectionState === 'registered' ? 'bg-green-500' :
          connectionState === 'connecting' ? 'bg-yellow-500 animate-pulse' :
          connectionState === 'error' ? 'bg-red-500' :
          'bg-gray-500'
        }`} />
        <span className={`text-sm font-medium ${
          connectionState === 'registered' ? 'text-green-400' :
          connectionState === 'connecting' ? 'text-yellow-400' :
          connectionState === 'error' ? 'text-red-400' :
          'text-gray-400'
        }`}>
          {connectionState === 'registered' ? 'Phone Ready' :
           connectionState === 'connecting' ? 'Connecting...' :
           connectionState === 'error' ? 'Connection Error' :
           'Disconnected'}
        </span>
      </div>

      {/* Line Status */}
      <div className="bg-virgo-navy-800 rounded-xl border border-virgo-navy-600 overflow-hidden">
        <div className="px-4 py-3 border-b border-virgo-navy-600 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-virgo-gold-400 flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Lines
          </h3>
        </div>
        <div className="p-3 space-y-2">
          {/* Line 1 - Primary */}
          <div className={`flex items-center gap-3 p-3 rounded-lg border ${
            callState !== 'idle'
              ? callState === 'incoming' ? 'bg-green-500/10 border-green-500/30 animate-pulse' :
                callState === 'hold' ? 'bg-yellow-500/10 border-yellow-500/30' :
                'bg-blue-500/10 border-blue-500/30'
              : 'bg-virgo-navy-700 border-virgo-navy-600'
          }`}>
            <div className={`w-3 h-3 rounded-full ${
              callState === 'active' ? 'bg-green-500 animate-pulse' :
              callState === 'hold' ? 'bg-yellow-500' :
              callState === 'incoming' ? 'bg-green-500 animate-pulse' :
              callState === 'calling' || callState === 'ringing' ? 'bg-blue-500 animate-pulse' :
              'bg-gray-500'
            }`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white flex items-center gap-2">
                Line 1
                {callState !== 'idle' && activeCall?.phoneNumber && (
                  <span className="text-xs text-virgo-teal-400 truncate">
                    • {activeCall.phoneNumber}
                  </span>
                )}
              </div>
              <div className="text-xs text-virgo-teal-500">
                {callState === 'idle' ? 'Available' :
                 callState === 'active' ? 'In Call' :
                 callState === 'hold' ? 'On Hold' :
                 callState === 'incoming' ? 'Incoming Call' :
                 callState === 'calling' ? 'Dialing...' :
                 callState === 'ringing' ? 'Ringing...' :
                 'Unknown'}
              </div>
            </div>
            {callState !== 'idle' && (
              <Headphones className={`w-4 h-4 ${
                callState === 'incoming' ? 'text-green-400' :
                callState === 'hold' ? 'text-yellow-400' :
                'text-blue-400'
              }`} />
            )}
          </div>

          {/* Line 2 - Secondary (placeholder for future multi-line) */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-virgo-navy-700 border border-virgo-navy-600 opacity-50">
            <div className="w-3 h-3 rounded-full bg-gray-500" />
            <div className="flex-1">
              <div className="text-sm font-medium text-white">Line 2</div>
              <div className="text-xs text-virgo-teal-500">Available</div>
            </div>
          </div>
        </div>
      </div>

      {/* Park Slots */}
      <div className="bg-virgo-navy-800 rounded-xl border border-virgo-navy-600 overflow-hidden">
        <div className="px-4 py-3 border-b border-virgo-navy-600">
          <h3 className="text-sm font-semibold text-virgo-gold-400 flex items-center gap-2">
            <Pause className="w-4 h-4" />
            Park Slots
          </h3>
        </div>
        <div className="p-3 grid grid-cols-3 gap-2">
          {parkSlots.map(slot => (
            <button
              key={slot.id}
              className={`p-3 rounded-lg border text-center transition-all ${
                slot.occupied
                  ? 'bg-yellow-500/20 border-yellow-500/30 hover:bg-yellow-500/30'
                  : 'bg-virgo-navy-700 border-virgo-navy-600 hover:bg-virgo-navy-600'
              }`}
              title={slot.occupied ? `Parked: ${slot.callerInfo}` : 'Empty slot'}
            >
              <div className="text-lg font-bold text-white">{slot.id}</div>
              <div className="text-xs text-virgo-teal-500">
                {slot.occupied ? 'Parked' : 'Empty'}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Call Forwarding */}
      <div className="bg-virgo-navy-800 rounded-xl border border-virgo-navy-600 overflow-hidden">
        <div className="px-4 py-3 border-b border-virgo-navy-600 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-virgo-gold-400 flex items-center gap-2">
            <PhoneForwarded className="w-4 h-4" />
            Forwarding
          </h3>
          <button
            onClick={() => setShowForwardingSettings(!showForwardingSettings)}
            className="p-1 hover:bg-virgo-navy-600 rounded"
          >
            <Settings className="w-4 h-4 text-virgo-teal-400" />
          </button>
        </div>
        <div className="p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-virgo-teal-400">
              {forwardingEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <button
              onClick={() => setForwardingEnabled(!forwardingEnabled)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                forwardingEnabled ? 'bg-cyan-500' : 'bg-virgo-navy-600'
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                forwardingEnabled ? 'left-7' : 'left-1'
              }`} />
            </button>
          </div>
          {showForwardingSettings && (
            <div className="mt-3 pt-3 border-t border-virgo-navy-600">
              <input
                type="tel"
                value={forwardNumber}
                onChange={(e) => setForwardNumber(e.target.value)}
                placeholder="Forward to number..."
                className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-navy-600 rounded-lg text-white text-sm placeholder-virgo-navy-400 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>
          )}
        </div>
      </div>

      {/* Team Extensions / BLF */}
      <div className="bg-virgo-navy-800 rounded-xl border border-virgo-navy-600 overflow-hidden">
        <div className="px-4 py-3 border-b border-virgo-navy-600 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-virgo-gold-400 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Team
          </h3>
          <button
            onClick={loadExtensions}
            className="p-1 hover:bg-virgo-navy-600 rounded"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 text-virgo-teal-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="p-2 max-h-64 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 text-virgo-teal-400 animate-spin" />
            </div>
          ) : extensions.length === 0 ? (
            <div className="text-center py-4 text-sm text-virgo-teal-500">
              No other extensions
            </div>
          ) : (
            <div className="space-y-1">
              {extensions.map(ext => (
                <div
                  key={ext.id}
                  className={`flex items-center gap-3 p-2 rounded-lg border transition-all ${getStatusBgColor(ext)} hover:bg-virgo-navy-600`}
                >
                  <div className={`w-2.5 h-2.5 rounded-full ${getStatusColor(ext)}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">
                      {ext.name || `Ext ${ext.extensionNumber}`}
                    </div>
                    <div className="text-xs text-virgo-teal-500 flex items-center gap-1">
                      <span>x{ext.extensionNumber}</span>
                      <span>•</span>
                      <span className={
                        ext.onCall ? 'text-red-400' :
                        ext.presence === 'Available' ? 'text-green-400' :
                        ext.presence === 'Busy' ? 'text-red-400' :
                        'text-gray-400'
                      }>
                        {getStatusLabel(ext)}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {ext.presence === 'Available' && !ext.onCall && (
                      <button
                        onClick={() => handleCallExtension({ extension: ext.extensionNumber })}
                        className="p-1.5 bg-green-500/20 hover:bg-green-500/30 rounded-lg transition-colors"
                        title="Call"
                      >
                        <Phone className="w-3.5 h-3.5 text-green-400" />
                      </button>
                    )}
                    {ext.onCall && (
                      <div className="p-1.5" title="On a call">
                        <Headphones className="w-3.5 h-3.5 text-red-400" />
                      </div>
                    )}
                    {callState === 'active' && (
                      <button
                        onClick={() => handleTransferTo({ extension: ext.extensionNumber })}
                        className="p-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg transition-colors"
                        title="Transfer"
                      >
                        <ArrowRight className="w-3.5 h-3.5 text-cyan-400" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* DND Toggle */}
      <div className="bg-virgo-navy-800 rounded-xl border border-virgo-navy-600 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
              <PhoneOff className="w-4 h-4 text-red-400" />
            </div>
            <div>
              <div className="text-sm font-medium text-white">Do Not Disturb</div>
              <div className="text-xs text-virgo-teal-500">Send calls to voicemail</div>
            </div>
          </div>
          <button
            onClick={() => {/* TODO: Toggle DND */}}
            className="relative w-12 h-6 rounded-full bg-virgo-navy-600 transition-colors"
          >
            <div className="absolute top-1 left-1 w-4 h-4 rounded-full bg-white" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhoneConsole;
